import { TokenInfo } from '@labb/constellation-core-types';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

import { useMergedConfig } from './MergedConfigProvider.tsx';

type MainContextValue = {
  isHelpDrawerOpen: boolean;
  setIsHelpDrawerOpen: (isOpen: boolean) => void;
  journeyId?: string;
  csrfToken: string;
  setCsrfToken: (token: string) => void;
  pegaToken?: TokenInfo;
  setPegaToken: (token?: TokenInfo) => void;
  urlSearchParams: URLSearchParams;
  setUrlSearchParams: (urlSearchParams: URLSearchParams) => void;
};

const MainContext = createContext<MainContextValue | undefined>(undefined);

export function MainProvider({ children }: { children: ReactNode }) {
  const { journeyId } = useMergedConfig();
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [pegaToken, setPegaToken] = useState<TokenInfo | undefined>();
  const [urlSearchParams, setUrlSearchParams] = useState<URLSearchParams>(new URLSearchParams());

  const value = useMemo<MainContextValue>(
    () => ({
      isHelpDrawerOpen,
      setIsHelpDrawerOpen,
      journeyId: journeyId || undefined,
      csrfToken,
      setCsrfToken,
      pegaToken,
      setPegaToken,
      urlSearchParams,
      setUrlSearchParams,
    }),
    [isHelpDrawerOpen, journeyId, csrfToken, pegaToken, urlSearchParams]
  );

  return <MainContext.Provider value={value}>{children}</MainContext.Provider>;
}

export function useMainContext(): MainContextValue {
  const context = useContext(MainContext);

  if (!context) {
    throw new Error('useMainContext must be used within a MainProvider');
  }

  return context;
}
